import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GeoLevel, Role, Position, UserStatus } from '../../types/database';
import {
  createUser,
  updateUser,
  getUserById,
  type UserPayload,
} from '../../services/userServices';
import {
  getSettings,
  type OfficeItem,
  type StateItem,
  type DistrictItem,
  type TownshipItem,
  type PermissionItem,
} from '../../services/settingServices';
import { useQueryClient } from '@tanstack/react-query';


interface UserFormProps {
  id?: string;
  initialData?: Partial<UserPayload>;
}

interface CurrentUserScope {
  scopeLevel?: GeoLevel | string;
  officeId?: string;
  stateId?: string;
  districtId?: string;
  townshipId?: string;
  allowedStateIds?: string[];
  allowedDistrictIds?: string[];
}

export function UserForm({ id: propId, initialData }: UserFormProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  // Extract ID from props or URL route parameters
  const userId = propId || params.id;
  const isEditMode = Boolean(userId);

  // Dynamic state loaded from userServices
  const [offices, setOffices] = useState<OfficeItem[]>([]);
  const [states, setStates] = useState<StateItem[]>([]);
  const [districts, setDistricts] = useState<DistrictItem[]>([]);
  const [townships, setTownships] = useState<TownshipItem[]>([]);
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);

  const [currentUser] = useState<CurrentUserScope | null>(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null') as CurrentUserScope | null;
    } catch {
      return null;
    }
  });

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!successMsg) return;

    const timeoutId = window.setTimeout(() => setSuccessMsg(null), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [successMsg]);

  const [formData, setFormData] = useState({
    employeeCode: initialData?.employeeCode || '',
    name: initialData?.name || '',
    email: initialData?.email || '',
    password: '',
    role: initialData?.role || Role.ADMIN,
    position: initialData?.position || Position.ATTORNEY,
    status: initialData?.status || UserStatus.ACTIVE,
    officeId: initialData?.officeId || '',
    scopeStateId: initialData?.scopeStateId || '',
    scopeDistrictId: initialData?.scopeDistrictId || '',
    scopeTownshipId: initialData?.scopeTownshipId || '',
  });

  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
    () => initialData?.permissionIds || []
  );

  // -------------------------------------------------------------
  // 1. Fetch States, Offices, and User Data via userService
  // -------------------------------------------------------------
  useEffect(() => {
    async function loadInitialData() {
      try {
        const settings = await getSettings();

        setStates(settings.data.states);
        setOffices(settings.data.offices);
        setDistricts(settings.data.districts);
        setTownships(settings.data.townships);
        setPermissions(settings.data.permissions);

        if (userId && !initialData) {
          const userData = await getUserById(userId);

          if (userData) {
            const stateId = userData.scopeStateId || '';
            const districtId = userData.scopeDistrictId || '';
            const townshipId = userData.scopeTownshipId || '';

            setFormData({
              employeeCode: userData.employeeCode || '',
              name: userData.name || '',
              email: userData.email || '',
              password: '',
              role: userData.role || Role.ADMIN,
              position: userData.position || Position.ATTORNEY,
              status: userData.status || UserStatus.ACTIVE,
              officeId: userData.officeId || '',
              scopeStateId: stateId,
              scopeDistrictId: districtId,
              scopeTownshipId: townshipId,
            });

            setSelectedPermissions(
              userData.permissions?.map((permission) => permission.permissionId) || []
            );
          }
        }
      } catch (err: any) {
        console.error('Failed to load form setup data:', err);
        setErrorMsg(err.message || 'အချက်အလက်များ ဒေါင်းလုဒ်လုပ်ရာတွင် အမှားအယွင်းရှိနေပါသည်');
      }
    }

    loadInitialData();
  }, [userId, initialData]);

  const allowedStateIds = currentUser?.allowedStateIds || [];
  const allowedDistrictIds = currentUser?.allowedDistrictIds || [];
  const isNationalUser = !currentUser || currentUser.scopeLevel === GeoLevel.NATIONAL;
  const currentOffice = offices.find((office) => office.id === currentUser?.officeId);
  const scopedStateIds = allowedStateIds.length > 0
    ? allowedStateIds
    : districts
        .filter((district) => allowedDistrictIds.includes(district.id))
        .map((district) => district.stateId);
  const hasExplicitScopeIds = scopedStateIds.length > 0 || allowedDistrictIds.length > 0;

  const visibleStates = isNationalUser || !hasExplicitScopeIds
    ? states
    : states.filter((state) => scopedStateIds.includes(state.id));

  const visibleDistricts = isNationalUser || !hasExplicitScopeIds
    ? districts
    : currentUser?.scopeLevel === GeoLevel.DISTRICT || currentUser?.scopeLevel === GeoLevel.TOWNSHIP
      ? districts.filter((district) => allowedDistrictIds.includes(district.id))
      : districts.filter((district) => scopedStateIds.includes(district.stateId));

  const visibleTownships = isNationalUser || !hasExplicitScopeIds
    ? townships
    : currentUser?.scopeLevel === GeoLevel.DISTRICT || currentUser?.scopeLevel === GeoLevel.TOWNSHIP
      ? townships.filter((township) => allowedDistrictIds.includes(township.districtId))
      : townships.filter((township) =>
          visibleDistricts.some((district) => district.id === township.districtId)
        );

  const visibleOffices = isNationalUser || !hasExplicitScopeIds
    ? offices
    : offices.filter((office) => {
        if (currentUser?.scopeLevel === GeoLevel.TOWNSHIP) {
          return office.townshipId === currentOffice?.townshipId;
        }
        if (currentUser?.scopeLevel === GeoLevel.DISTRICT) {
          return allowedDistrictIds.includes(office.districtId || '');
        }
        return scopedStateIds.includes(office.stateId || '');
      });

  useEffect(() => {
    if (isNationalUser || userId) return;

    setFormData((previous) => {
      const nextStateId = previous.scopeStateId ||
        (visibleStates.length === 1 ? visibleStates[0].id : '');
      const stateDistricts = visibleDistricts.filter(
        (district) => district.stateId === nextStateId
      );
      const nextDistrictId = previous.scopeDistrictId ||
        (currentUser?.scopeLevel === GeoLevel.TOWNSHIP && stateDistricts.length === 1
          ? stateDistricts[0].id
          : '');

      if (
        nextStateId === previous.scopeStateId &&
        nextDistrictId === previous.scopeDistrictId
      ) {
        return previous;
      }

      return {
        ...previous,
        scopeStateId: nextStateId,
        scopeDistrictId: nextDistrictId,
      };
    });
  }, [isNationalUser, userId, currentUser?.scopeLevel, visibleStates, visibleDistricts]);

  // -------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------
  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextRole = e.target.value as Role;
    setFormData({ ...formData, role: nextRole });
  };

  const handlePermissionToggle = (permissionId: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);
    setSubmitting(true);

    const scopeLevel: GeoLevel = formData.scopeTownshipId
      ? GeoLevel.TOWNSHIP
      : formData.scopeDistrictId
      ? GeoLevel.DISTRICT
      : formData.scopeStateId
      ? GeoLevel.STATE
      : GeoLevel.NATIONAL;

    const payload: UserPayload = {
      employeeCode: formData.employeeCode,
      name: formData.name,
      email: formData.email,
      ...(formData.password ? { password: formData.password } : {}),
      role: formData.role,
      position: formData.position,
      status: formData.status,
      scopeLevel,
      officeId: formData.officeId || undefined,
      scopeStateId: formData.scopeStateId || undefined,
      scopeDistrictId: formData.scopeDistrictId || undefined,
      scopeTownshipId: formData.scopeTownshipId || undefined,
      permissionIds: selectedPermissions,
    };

    try {
      if (isEditMode && userId) {
        await updateUser(userId, payload);
      } else {
        await createUser(payload);
      }
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setSuccessMsg(
        isEditMode
          ? 'အသုံးပြုသူအချက်အလက်များကို အောင်မြင်စွာ ပြင်ဆင်ပြီးပါပြီ။'
          : 'အသုံးပြုသူအကောင့်ကို အောင်မြင်စွာ ဖန်တီးပြီးပါပြီ။'
      );
    } catch (err: any) {
      console.error('Failed to save user:', err);
      setErrorMsg(err.message || 'ပြင်ဆင်မှုကို သိမ်းဆည်း၍မရပါ (Failed to save user)');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackNavigation =async () => {
    await queryClient.invalidateQueries({ queryKey: ['ploFormOne'] });
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/plo-form-one');
    }
  };

  const permissionGroups = permissions.reduce<Record<string, PermissionItem[]>>(
    (groups, permission) => {
      const category = permission.name.split(':')[0] || 'other';
      (groups[category] ??= []).push(permission);
      return groups;
    },
    {}
  );

  return (
    <div className="mx-auto max-w-3xl space-y-6 font-sans antialiased pb-12">
      
      <div>

          <button
        type="button"
        onClick={handleBackNavigation}
        className="my-2 flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 font-medium text-xs shadow-sm transition"
        title="နောက်သို့ ပြန်သွားမည်"
      >
        ← နောက်သို့ ပြန်သွားမည်
      </button>
        <h2 className="text-xl font-bold text-slate-900">
          {isEditMode ? 'အသုံးပြုသူအကောင့် ပြင်ဆင်ခြင်း' : 'အသုံးပြုသူအကောင့်အသစ် ပြုလုပ်ခြင်း'}
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          {isEditMode
            ? 'လက်ရှိအသုံးပြုသူ၏ အခန်းကဏ္ဍ၊ လုပ်ပိုင်ခွင့်များနှင့် နယ်မြေအတိုင်းအတာကို ပြင်ဆင်သတ်မှတ်ခြင်း'
            : 'လုပ်ဆောင်နိုင်သော အဖွဲ့အစည်းဆိုင်ရာ အခန်းကဏ္ဍများကို သတ်မှတ်ခြင်း နှင့် လုံခြုံသော အရင်းအမြစ် အတိုင်းအတာများကို ပြင်ဆင်သတ်မှတ်ခြင်း'}
        </p>
      </div>

      {successMsg && (
        <div
          role="status"
          aria-live="polite"
          className="fixed right-4 top-4 z-50 flex max-w-md items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 shadow-lg"
        >
          <span className="font-medium">{successMsg}</span>
          <button
            type="button"
            onClick={() => setSuccessMsg(null)}
            aria-label="ပိတ်ရန်"
            className="ml-auto text-lg leading-none text-green-700 hover:text-green-900"
          >
            ×
          </button>
        </div>
      )}

      {errorMsg && (
        <div className="rounded-lg bg-red-50 p-4 border border-red-200 text-sm text-red-700">
          {errorMsg}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        {/* Section 1: User Profile Info */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-bold tracking-wider text-slate-600 mb-1.5">
              ဝန်ထမ်းကုဒ် (Employee Code)
            </label>
            <input
              type="text"
              required
              placeholder="EMP-001"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none"
              value={formData.employeeCode}
              onChange={(e) => setFormData({ ...formData, employeeCode: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-xs font-bold tracking-wider text-slate-600 mb-1.5">
              အမည်အပြည့်အစုံ (Full Name)
            </label>
            <input
              type="text"
              required
              placeholder="ဥပမာ - ဦးသူရကျော်"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-xs font-bold tracking-wider text-slate-600 mb-1.5">
              အီးမေးလ်လိပ်စာ (Email Address)
            </label>
            <input
              type="email"
              required
              disabled={isEditMode}
              placeholder="name@legalone.com"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none disabled:bg-slate-50"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          {!isEditMode && (
            <div>
              <label className="block text-xs font-bold tracking-wider text-slate-600 mb-1.5">
                စကားဝှက် (Password)
              </label>
              <input
                type="password"
                required={!isEditMode}
                placeholder="••••••••"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-bold tracking-wider text-slate-600 mb-1.5">
              ရုံး / ဌာန (Office)
            </label>
            <select
              required
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none"
              value={formData.officeId}
              onChange={(e) => setFormData({ ...formData, officeId: e.target.value })}
            >
              <option value="">ရုံးရွေးချယ်ပါ (Select Office)</option>
              {visibleOffices.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold tracking-wider text-slate-600 mb-1.5">
              ရာထူး (Position)
            </label>
            <select
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value as Position })}
            >
              <option value="CHIEF_ATTORNEY">Chief Attorney (ဥပဒေချုပ်)</option>
              <option value="ATTORNEY">Attorney (ဥပဒေအရာရှိ)</option>
              <option value="AUDITOR">Auditor (စာရင်းစစ်)</option>
              <option value="CLERK">Clerk (စာရေး)</option>
              <option value="SECRETARY">Secretary (အတွင်းရေးမှူး)</option>
              <option value="STAFF">Staff (ဝန်ထမ်း)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold tracking-wider text-slate-600 mb-1.5">
              စနစ် အခန်းကဏ္ဍ (Role)
            </label>
            <select
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none"
              value={formData.role}
              onChange={handleRoleChange}
            >
              <option value={Role.SUPER_ADMIN}>Super Admin (စနစ်တစ်ခုလုံး ထိန်းချုပ်ခွင့်)</option>
              <option value={Role.ADMIN}>Admin (စီမံခန့်ခွဲသူ)</option>
              <option value={Role.USER}>User (ပုံမှန် အသုံးပြုသူ)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold tracking-wider text-slate-600 mb-1.5">
              အကောင့် အခြေအနေ (Status)
            </label>
            <select
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as UserStatus})}
            >
              <option value={UserStatus.ACTIVE}>Active (အသုံးပြုနိုင်သည်)</option>
              <option value={UserStatus.DISABLED}>Disabled (ပိတ်ထားသည်)</option>
              <option value={UserStatus.LOCKED}>Locked (သော့ခတ်ထားသည်)</option>
            </select>
          </div>
        </div>

        <hr className="border-slate-200" />

        {/* Section 2: Geographic Scope */}
<div className="space-y-4">
  <div>
    <h3 className="text-sm font-bold text-slate-900">
      ဒေသန္တရစီမံခန့်ခွဲမှု အတိုင်းအတာ (Administrative Scope)
    </h3>
    <p className="text-xs text-slate-500">
      အသုံးပြုသူ လုပ်ပိုင်ခွင့်ရှိမည့် ပထဝီဝင်နယ်မြေ ကန့်သတ်ချက်ကို ရွေးချယ်ပါ။
    </p>
  </div>

  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
    {/* State Selection */}
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1">
        တိုင်းဒေသကြီး/ပြည်နယ်
      </label>
      <select
        required={!isNationalUser}
        disabled={currentUser?.scopeLevel === GeoLevel.DISTRICT || currentUser?.scopeLevel === GeoLevel.TOWNSHIP}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:opacity-70"
        value={formData.scopeStateId}
        onChange={(e) =>
          setFormData({
            ...formData,
            scopeStateId: e.target.value,
            scopeDistrictId: '',
            scopeTownshipId: '',
          })
        }
      >
        {isNationalUser && <option value="">တစ်နိုင်ငံလုံး (NATIONAL)</option>}
        {visibleStates.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>
    </div>

    {/* District Selection */}
    <div
      className={`transition-opacity duration-200 ${
        !formData.scopeStateId ? 'opacity-40' : 'opacity-100'
      }`}
    >
      <label className="block text-xs font-semibold text-slate-700 mb-1">
        ခရိုင်
      </label>
      <select
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:opacity-70"
        disabled={!formData.scopeStateId || currentUser?.scopeLevel === GeoLevel.TOWNSHIP}
        value={formData.scopeDistrictId}
        onChange={(e) =>
          setFormData({
            ...formData,
            scopeDistrictId: e.target.value,
            scopeTownshipId: '',
          })
        }
      >
        <option value="">ခရိုင်အားလုံး (All Districts)</option>
        {visibleDistricts
          .filter((district) => !formData.scopeStateId || district.stateId === formData.scopeStateId)
          .map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>
    </div>

    {/* Township Selection */}
    <div
      className={`transition-opacity duration-200 ${
        !formData.scopeDistrictId ? 'opacity-40' : 'opacity-100'
      }`}
    >
      <label className="block text-xs font-semibold text-slate-700 mb-1">
        မြို့နယ်
      </label>
      <select
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:opacity-70"
        disabled={!formData.scopeDistrictId}
        value={formData.scopeTownshipId}
        onChange={(e) =>
          setFormData({ ...formData, scopeTownshipId: e.target.value })
        }
      >
        <option value="">မြို့နယ်အားလုံး (All Townships)</option>
        {visibleTownships
          .filter((township) => !formData.scopeDistrictId || township.districtId === formData.scopeDistrictId)
          .map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
    </div>
  </div>
</div>

        <hr className="border-slate-200" />

        {/* Section 3: Permissions */}
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-bold text-slate-900">လုပ်ပိုင်ခွင့်အသေးစိတ် ပြင်ဆင်သတ်မှတ်ခြင်း (Permissions)</h3>
          </div>

          <div className="space-y-6">
            {Object.entries(permissionGroups).map(([category, items]) => (
              <div key={category} className="space-y-3">
                <h4 className="text-xs font-bold tracking-wider text-blue-700 bg-blue-50/60 px-2 py-1 rounded">
                  {category}
                </h4>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {items.map((item) => {
                    const isChecked = selectedPermissions.includes(item.id);
                    return (
                      <label
                        key={item.id}
                        className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                          isChecked
                            ? 'border-blue-200 bg-blue-50/20'
                            : 'border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600"
                          checked={isChecked}
                          onChange={() => handlePermissionToggle(item.id)}
                        />
                        <div className="text-sm">
                          <span className="block font-bold text-slate-800">{item.name}</span>
                          <span className="block text-xs text-slate-500 font-normal mt-1">{item.description}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form Controls */}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            မလုပ်တော့ပါ (Cancel)
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {submitting 
              ? 'လုပ်ဆောင်နေသည်...' 
              : isEditMode 
                ? 'ပြင်ဆင်မှုများကို သိမ်းဆည်းမည် (Save)' 
                : 'အကောင့်ပြုလုပ်မည် (Create)'}
          </button>
        </div>
      </form>
    </div>
  );
}